import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Zap, ChevronLeft, Check, SkipForward, HelpCircle, ArrowLeft } from "lucide-react";
import { API } from "../App";
import {
  CATEGORY_DISPLAY_NAMES,
  CATEGORY_GROUPS,
  TAX_DEDUCTIBLE_CATEGORIES,
  TAX_GUIDANCE_RULES,
  ATO_LAST_REVIEWED,
} from "../utils/categoryUtils";

function getCategoryName(cat) {
  if (!cat) return "Uncategorized";
  return CATEGORY_DISPLAY_NAMES[cat] || cat.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

function fmt(n) {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);
}

function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("taxbuddy_token")}` };
}

const ALL_GROUPS = {
  ...CATEGORY_GROUPS,
  "Not Sure": ["personal_other"],
};
const ALL_DISPLAY_NAMES = {
  ...CATEGORY_DISPLAY_NAMES,
  personal_other: "Personal Expense (not deductible)",
};

export default function QuickSortMode() {
  const navigate = useNavigate();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showGuidance, setShowGuidance] = useState(null);
  const [noteText, setNoteText] = useState("");

  const txnsRef = useRef([]);

  useEffect(() => {
    axios
      .get(`${API}/api/transactions?category=uncategorized&limit=2000`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const raw = res.data;
        const data = Array.isArray(raw) ? raw : (raw.transactions || []);
        txnsRef.current = data;
        setTxns(data);
      })
      .catch(() => toast.error("Could not load transactions"))
      .finally(() => setLoading(false));
  }, []);

  const close = () => navigate("/transactions");

  const isReceiptOnly = (t) =>
    !(t.merchant) && /^(visa|eftpos|direct\s+debit)\s+(purchase|debit)?\s*[-–]\s*receipt\s+\d+/i.test(t.description || "");

  const patch = (txId, cat) =>
    axios.patch(
      `${API}/api/transactions/${txId}`,
      { category: cat, ...(noteText.trim() ? { notes: noteText.trim() } : {}) },
      { headers: getAuthHeaders() }
    );

  const handleCategory = async (cat) => {
    if (busy) return;
    const current = txnsRef.current[idx];
    if (!current) return;
    setBusy(true);
    try {
      await patch(current.id || current._id, cat);
      toast.success(`Saved as ${getCategoryName(cat)}`, { duration: 1200 });
      setSavedCount((s) => s + 1);
      setIdx((i) => i + 1);
      setShowGuidance(null);
      setNoteText("");
    } catch {
      toast.error("Failed to save — please try again");
    } finally {
      setBusy(false);
    }
  };

  const handleApplyAll = async (cat) => {
    if (busy) return;
    const snapIdx = idx;
    const snap = txnsRef.current;
    const current = snap[snapIdx];
    if (!current) return;
    setBusy(true);
    const name = current.merchant || current.description;
    const matching = snap.slice(snapIdx).filter(
      (t) => (t.merchant || t.description) === name
    );
    let saved = 0;
    for (const tx of matching) {
      try {
        await patch(tx.id || tx._id, cat);
        saved++;
      } catch {}
    }
    toast.success(
      `Applied "${getCategoryName(cat)}" to ${saved} transaction${saved !== 1 ? "s" : ""}`,
      { duration: 2000 }
    );
    setSavedCount((s) => s + saved);
    setIdx(snapIdx + saved);
    setShowGuidance(null);
    setNoteText("");
    setBusy(false);
  };

  const handleBulkReceipts = async (cat) => {
    if (busy) return;
    const snap = txnsRef.current;
    const receipts = snap.filter(isReceiptOnly);
    if (!receipts.length) return;
    setBusy(true);
    let saved = 0;
    for (const tx of receipts) {
      try {
        await patch(tx.id || tx._id, cat);
        saved++;
      } catch {}
    }
    toast.success(`Applied "${getCategoryName(cat)}" to ${saved} receipt-only transactions`, { duration: 3000 });
    setSavedCount((s) => s + saved);
    const remaining = snap.filter((t) => !isReceiptOnly(t));
    txnsRef.current = remaining;
    setTxns(remaining);
    setIdx(0);
    setShowGuidance(null);
    setNoteText("");
    setBusy(false);
  };

  const handleSkip = () => {
    setIdx((i) => i + 1);
    setShowGuidance(null);
    setNoteText("");
  };

  const handleBack = () => {
    setIdx((i) => Math.max(0, i - 1));
    setShowGuidance(null);
    setNoteText("");
  };

  if (loading) {
    return (
      <div style={pageWrap}>
        <div style={card}>
          <button onClick={close} style={backBtn}>
            <ArrowLeft size={14} /> Back to transactions
          </button>
          <p style={{ color: "#6B7280", fontSize: 14, textAlign: "center", padding: "40px 0" }}>
            Loading transactions…
          </p>
        </div>
      </div>
    );
  }

  const total = txns.length;
  const current = txns[idx];

  if (total === 0 || !current) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{
            width: 64, height: 64, background: "#ECFDF5", borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px",
          }}>
            <Check size={32} color="#059669" />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 8 }}>
            {total === 0 ? "Nothing to sort!" : "All done!"}
          </h2>
          <p style={{ color: "#6B7280", fontSize: 14, marginBottom: 24 }}>
            {total === 0
              ? "All transactions are already categorised."
              : `You categorised ${savedCount} of ${total} transactions.`}
          </p>
          <button onClick={close} style={btnGreen}>
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  const isCredit = current.type === "credit";
  const progress = Math.min((idx / total) * 100, 100);
  const currentIsReceipt = isReceiptOnly(current);
  const receiptCount = txns.filter(isReceiptOnly).length;
  const allReceiptCount = currentIsReceipt ? receiptCount : 0;
  const similarCount = currentIsReceipt ? 0 : txns
    .slice(idx + 1)
    .filter(
      (t) => (t.merchant || t.description) === (current.merchant || current.description)
    ).length;

  return (
    <div style={pageWrap}>
      <div style={card}>
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px", borderBottom: "1px solid #E5E7EB",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Zap size={18} color="#D97706" />
            <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Quick Sort</span>
            <span style={{
              fontSize: 12, color: "#6B7280", background: "#F3F4F6",
              borderRadius: 99, padding: "2px 8px",
            }}>
              {idx + 1} / {total}
            </span>
          </div>
          <button onClick={close} style={backBtn}>
            <ArrowLeft size={14} /> Exit
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: "#E5E7EB" }}>
          <div style={{
            height: 4, background: "#1A4D2E",
            width: `${progress}%`, transition: "width 0.3s",
          }} />
        </div>

        {/* Transaction card */}
        <div style={{ padding: "20px 20px 0" }}>
          <div style={{
            background: "#F9FAFB", borderRadius: 12, padding: 16,
            marginBottom: 10, border: "1px solid #E5E7EB",
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", marginBottom: 4,
            }}>
              <div style={{ flex: 1, paddingRight: 12 }}>
                {current.merchant ? (
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{current.merchant}</div>
                ) : (
                  <div style={{ fontSize: 11, color: "#9CA3AF", fontStyle: "italic", marginBottom: 2 }}>No merchant on record</div>
                )}
                <div style={{ fontSize: 12, color: "#374151", marginTop: 2 }}>{current.description}</div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: isCredit ? "#059669" : "#DC2626", flexShrink: 0 }}>
                {isCredit ? "+" : "-"}{fmt(current.amount)}
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>
              {current.date?.slice(0, 10)} · {current.bank || "Bank statement"}
            </div>
          </div>

          {currentIsReceipt && (
            <div style={{
              background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8,
              padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#92400E",
            }}>
              🧾 ING receipt — no merchant stored in PDF. {allReceiptCount} receipt-only transaction{allReceiptCount !== 1 ? "s" : ""} total.
              <div style={{ fontSize: 11, color: "#B45309", marginTop: 3 }}>
                Pick a category below to apply to <strong>all {allReceiptCount}</strong> receipts at once.
              </div>
            </div>
          )}

          {similarCount > 0 && (
            <div style={{
              background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8,
              padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#1E40AF",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span>📋 {similarCount} more transaction{similarCount > 1 ? "s" : ""} with this name</span>
              <span style={{ fontSize: 11, color: "#3B82F6" }}>Click a category to apply to all</span>
            </div>
          )}
        </div>

        {/* Note field */}
        <div style={{ padding: "0 20px 10px" }}>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="What is this for? (optional — e.g. 'work laptop', 'client dinner')"
            rows={2}
            style={{
              width: "100%", boxSizing: "border-box", fontSize: 12, color: "#374151",
              border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px",
              resize: "none", outline: "none", fontFamily: "inherit",
              background: noteText ? "#FFFBEB" : "#F9FAFB",
            }}
          />
        </div>

        {/* Category buttons */}
        <div style={{ padding: "0 20px", overflowY: "auto", maxHeight: 300 }}>
          {Object.entries(ALL_GROUPS).map(([groupName, cats]) => (
            <div key={groupName} style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: "#9CA3AF",
                textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
              }}>
                {groupName}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {cats.map((cat) => {
                  const isSelected = current.category === cat;
                  const isDed = TAX_DEDUCTIBLE_CATEGORIES.includes(cat);
                  const guidance = TAX_GUIDANCE_RULES[cat];
                  return (
                    <div key={cat} style={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <button
                        onClick={() => currentIsReceipt ? handleBulkReceipts(cat) : similarCount > 0 ? handleApplyAll(cat) : handleCategory(cat)}
                        disabled={busy}
                        style={{
                          padding: "5px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                          border: isSelected ? "2px solid #1A4D2E" : "1px solid #E5E7EB",
                          background: isSelected ? "#ECFDF5" : cat === "personal_other" ? "#F9FAFB" : "#fff",
                          color: isSelected ? "#065F46" : cat === "personal_other" ? "#6B7280" : "#374151",
                          cursor: busy ? "not-allowed" : "pointer",
                          display: "flex", alignItems: "center", gap: 4,
                          opacity: busy ? 0.6 : 1,
                        }}
                      >
                        {isDed && <span style={{ color: "#2563EB", fontSize: 10 }}>✓</span>}
                        {ALL_DISPLAY_NAMES[cat] || getCategoryName(cat)}
                        {currentIsReceipt && (
                          <span style={{ fontSize: 9, color: "#B45309" }}> (×{allReceiptCount})</span>
                        )}
                        {!currentIsReceipt && similarCount > 0 && (
                          <span style={{ fontSize: 9, color: "#9CA3AF" }}> (×{similarCount + 1})</span>
                        )}
                      </button>
                      {guidance && (
                        <button
                          onClick={() => setShowGuidance(showGuidance === cat ? null : cat)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 2 }}
                        >
                          <HelpCircle size={12} />
                        </button>
                      )}
                      {showGuidance === cat && (
                        <div style={{
                          position: "absolute", background: "#EFF6FF", border: "1px solid #BFDBFE",
                          borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#1E40AF",
                          maxWidth: 280, zIndex: 10,
                        }}>
                          💡 {TAX_GUIDANCE_RULES[cat]}
                          <div style={{ fontSize: 10, color: "#93C5FD", marginTop: 4 }}>
                            ATO reviewed: {ATO_LAST_REVIEWED}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: "1px solid #E5E7EB",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 8,
        }}>
          <button
            onClick={handleBack}
            disabled={idx === 0}
            style={{ ...btnGhost, opacity: idx === 0 ? 0.3 : 1 }}
          >
            <ChevronLeft size={14} /> Back
          </button>
          {receiptCount > 0 && (
            <button
              onClick={() => handleBulkReceipts("personal_other")}
              disabled={busy}
              style={{
                ...btnGhost,
                color: "#92400E", borderColor: "#FED7AA",
                background: "#FFF7ED", fontSize: 11,
                opacity: busy ? 0.6 : 1,
              }}
            >
              Mark all {receiptCount} receipt-only as Personal
            </button>
          )}
          <button onClick={handleSkip} style={btnGhost}>
            <SkipForward size={14} /> Skip
          </button>
        </div>
      </div>
    </div>
  );
}

const pageWrap = {
  display: "flex",
  justifyContent: "center",
  padding: "24px 16px",
  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
};

const card = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
  border: "1px solid #E5E7EB",
  width: "100%",
  maxWidth: 520,
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100vh - 120px)",
  overflow: "hidden",
};

const backBtn = {
  background: "none",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  color: "#6B7280",
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontFamily: "inherit",
};

const btnGreen = {
  background: "#1A4D2E",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
};

const btnGhost = {
  background: "none",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  color: "#6B7280",
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontFamily: "inherit",
};
